import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Plus, X, Loader2, PhoneOff, Check } from 'lucide-react';
import { INPUT, LABEL, Pill, cx } from '../contacts/ui';

const API = process.env.REACT_APP_BACKEND_URL;
const SRC = { Lead: 'bg-[var(--accent-soft)] text-[var(--accent-ink)]', 'E-sign': 'bg-[var(--violet-bg)] text-[var(--violet-ink)]', 'Client file': 'bg-[var(--green-bg)] text-[var(--green-ink)]' };

// Search every visible contact (clients, co-applicants, leads, signers) and hand-add them to the queue.
export const ClientSearch = ({ headers, added = [], onAdd, onRemove, isIn, compact }) => {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return undefined; }
    setRes('loading');
    const t = setTimeout(() => {
      axios.get(`${API}/api/calling/search`, { headers, params: { q } }).then((r) => setRes(r.data.items || [])).catch(() => setRes([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, headers]);

  return (
    <div className={compact ? '' : 'mt-5'} data-testid="calling-client-search">
      {!compact && <p className={LABEL}>Add any contact</p>}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--faint)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile or email" data-testid="calling-search-input" className={cx(INPUT, 'pl-9')} />
      </div>
      {res === 'loading' && <p className="mt-2 flex items-center gap-2 text-xs text-[var(--muted)]"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…</p>}
      {Array.isArray(res) && !res.length && <p className="mt-2 text-xs text-[var(--muted)]" data-testid="calling-search-empty">No contacts match "{q}".</p>}
      {Array.isArray(res) && !!res.length && (
        <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-xl border border-[var(--line)] bg-[var(--surface-2)] p-1.5" data-testid="calling-search-results">
          {res.map((r) => {
            const inQ = isIn(r.key);
            return (
              <li key={r.key} className="flex items-center gap-3 rounded-lg bg-[var(--surface)] px-3 py-2">
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5"><span className="truncate text-sm font-semibold text-[var(--ink)]">{r.name}</span>
                    <Pill className={SRC[r.source] || SRC['Client file']} data-testid={`calling-search-source-${r.key}`}>{r.source}</Pill></span>
                  <span className="block truncate text-xs text-[var(--muted)]">{[r.status_label, r.reference, r.broker_name].filter(Boolean).join(' · ')}</span>
                </span>
                {!r.has_mobile && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[var(--red)]"><PhoneOff className="h-3 w-3" />No mobile</span>}
                {inQ ? <span className="inline-flex items-center gap-1 rounded-full bg-[var(--green-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--green-ink)]" data-testid={`calling-search-inqueue-${r.key}`}><Check className="h-3.5 w-3.5" />In queue</span> : (
                  <button type="button" onClick={() => onAdd(r)} data-testid={`calling-search-add-${r.key}`} className="inline-flex items-center gap-1 rounded-full bg-[var(--solid)] px-3 py-1.5 text-xs font-semibold text-[var(--on-solid)] active:scale-95"><Plus className="h-3.5 w-3.5" />Add</button>)}
              </li>
            );
          })}
        </ul>
      )}
      {!!added.length && (
        <div className="mt-3" data-testid="calling-added-list">
          <p className="mb-1.5 text-xs font-semibold text-[var(--muted)]">Added by you · {added.length}</p>
          <ul className="flex flex-wrap gap-1.5">
            {added.map((a) => (
              <li key={a.key} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-[var(--accent-soft)] py-1 pl-3 pr-1 text-xs font-semibold text-[var(--accent-ink)]" data-testid={`calling-added-${a.key}`}>
                {a.name}
                <button type="button" onClick={() => onRemove(a.key)} aria-label={`Remove ${a.name}`} data-testid={`calling-added-remove-${a.key}`} className="rounded-full p-0.5 hover:bg-[var(--hover)]"><X className="h-3.5 w-3.5" /></button>
              </li>))}
          </ul>
        </div>
      )}
    </div>
  );
};
